import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { inventoryAppliedFrame, pushInventoryTruth } from '../shared/inventory-applied-truth';
import contract from '../janus-engine/compile/c1-contract.json';
import { ecId, ecLean, HandBack, itemField, record, upper, type Row } from '../janus-engine/ability/context.js';
import { exchangeCoinStacks, exchangeInventoryRows, exchangePanelRefusal, favorPrices, favorTier, hasHaggled, planTrade, planTraining, planHaggle, pricingFavor, trainingVerdict, type ExchangeFacts, type ExchangeContext, type ExchangeInput, type ExchangePanelFacts, type ExchangeSubmit } from '../janus-engine/inventory/trade-exchange.js';
import { buildExchangeCommand, exchangeOutcomeUnknown, pendingExchange, postExchange, type ExchangeAnswer, type ExchangeCommand } from '../janus-engine/inventory/trade-adjust.js';
import { consentPending, TRIGGER_CONSENT_REASON } from '../janus-engine/ability/trigger-consent.js';
import { planWithConsent, offerFortunate } from './trigger-consent.js';
import { activityContext, decideActivity } from './activity-truth.js';
import { actorRowFor } from './movement-truth.js';
import { fetchAndCacheCaster } from './caster-cache.js';
import { fetchAndCacheObject } from './object-cache.js';
import { runRowOnly } from '../shared/actor-row-loader.js';

interface PanelInput { encounterId: string; playerId: string; counterpartId: string }
interface DeferredFortunate { command: ExchangeCommand; ids: string[]; saved: boolean; committed: boolean; delivering?: boolean; timer?: ReturnType<typeof setTimeout> }
const deferredFortunate = new Map<string, DeferredFortunate>();
function retireOffers(pending: DeferredFortunate): void {
  clearTimeout(pending.timer); deferredFortunate.delete(pending.command.operation_id);
}
async function sendOffers(pending: DeferredFortunate, fresh = false): Promise<void> {
  if (deferredFortunate.get(pending.command.operation_id) !== pending || !pending.saved || !pending.committed || pending.delivering) return;
  pending.delivering = true;
  try {
    // The committed frame may outrun asynchronous roster adoption. Read saved resources, including any later spend, before offering.
    if (!fresh) await refreshOfferRecipients(pending);
    if (deferredFortunate.get(pending.command.operation_id) !== pending) return;
    retireOffers(pending);
    offerFortunate(pending.command.encounter_id, pending.command.actor_id, pending.ids);
  } catch {
    pending.timer = setTimeout(() => { void recoverOffers(pending); }, 1000);
  } finally {
    pending.delivering = false;
  }
}
const refreshOfferRecipients = (pending: DeferredFortunate): Promise<unknown> => Promise.all(pending.ids.map(id =>
  fetchAndCacheCaster(id, { skipEncounterSeed: true, throwOnError: true, participantDeadline: true })));
window.addEventListener('ft:inventory-trade-truth', event => {
  const detail = record((event as CustomEvent).detail), frame = record(detail?.frame);
  const pending = deferredFortunate.get(String(frame?.operation_id ?? ''));
  if (pending && detail?.kind === 'committed') { pending.committed = true; void sendOffers(pending); }
});
/** A replay may have missed its committed frame. A fresh participant revision also proves the post-flush fan-out reached storage. */
async function recoverOffers(pending: DeferredFortunate, attempt = 0): Promise<void> {
  if (!deferredFortunate.has(pending.command.operation_id)) return;
  try {
    await refreshOfferRecipients(pending);
    if (deferredFortunate.get(pending.command.operation_id) !== pending) return;
    pending.committed ||= pending.ids.every(id => {
      const actor = record(actorRowFor(pending.command.encounter_id, id));
      return actor && upper(ecLean(actor, 'movement_last_operation_id')) === pending.command.operation_id;
    });
    await sendOffers(pending, true);
  } catch { /* A later committed frame or the next bounded refresh can still deliver the offer. */ }
  if (deferredFortunate.has(pending.command.operation_id) && attempt < 2)
    pending.timer = setTimeout(() => { void recoverOffers(pending, attempt + 1); }, 1000);
}
async function deliver(command: ExchangeCommand): Promise<ExchangeAnswer> {
  const favor = command.plan.writes.find(write => write.table === 'encounter_creatures'
    && upper(write.id) === command.counterpart_id && 'merchant_favor' in write.after)?.after.merchant_favor ?? command.before.merchant_favor;
  pushInventoryTruth('janus_inventory_trade', { ...command, merchant_favor: favor }, inventoryAppliedFrame(command, command.plan.writes));
  const answer = await postExchange(command, (endpoint, init) => saveAppliedTruth(String(endpoint), command, init!));
  const pending = deferredFortunate.get(command.operation_id);
  if (pending && !exchangeOutcomeUnknown(answer)) {
    if (!answer.ok) retireOffers(pending);
    else {
      pending.saved = true; void sendOffers(pending);
      if (!pending.committed) pending.timer = setTimeout(() => { void recoverOffers(pending); }, 1000);
    }
  }
  return answer;
}
const priceReaders: ExchangeContext['read'] = {
  inventoryField: (r, k) => r ? ftInventoryField(r, k) : null, value: (r, k) => r ? ftLayerValue(r, k) : null,
  itemType: r => String(ftItemsCol(r, 'item_type') ?? ''), itemId: r => String(ftItemsCol(r, 'item_id') ?? ''),
  computed: (r, g, k) => ftComputedValue(r, g, k), statusItemIds: r => ftCreatureStatuses(r, 'target').map(s => upper(s.status_item_id)),
};
function coinThumbnail(row: Row): string {
  // Same pointer/gallery readers and priority as inventory/thumbnail.ts; no picture fetches.
  for (const type of ['inventory_picture', 'inventory_presentation_picture', 'inventory_thumbnail_picture']) {
    const pic = ftPicturesOf(row, type)[0]; if (pic) return String(pictureField0(pic, 'picture_url') ?? '');
  }
  const pictures = ftItemPictures(row), pic = pictures.find(p => pictureField0(p, 'primary_picture') == 1) ?? pictures[0];
  return String(pictureField0(pic, 'picture_url') ?? '');
}
async function snapshot(input: PanelInput): Promise<ExchangeFacts> {
  const response = await fetch('/options/api/inventory_exchange_state_inc.php', { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ actor_id: input.playerId, counterpart_id: input.counterpartId, encounter_id: input.encounterId }) });
  const raw = record(await response.json());
  if (!response.ok || raw?.ok !== true || !Object.prototype.hasOwnProperty.call(raw, 'merchant_favor') || !Array.isArray(raw.history) || !Array.isArray(raw.charge_rows) || !Array.isArray(raw.status_rows) || !Object.prototype.hasOwnProperty.call(raw, 'prior_training'))
    throw new Error(String(raw?.error ?? 'Could not load exchange state.'));
  return { rawFavor: raw.merchant_favor as number | null, economy: itemField(record(raw.campaign), 'campaign_economy_difficulty'),
    campaignId: String(itemField(record(raw.campaign), 'campaign_id') ?? ''), history: raw.history as Row[], chargeRows: raw.charge_rows as Row[], statusRows: raw.status_rows as Row[], priorTraining: record(raw.prior_training) };
}
async function participants(input: PanelInput): Promise<{ player: Row; counterpart: Row }> {
  await Promise.all([fetchAndCacheCaster(input.playerId, { skipEncounterSeed: true, throwOnError: true }), fetchAndCacheCaster(input.counterpartId, { skipEncounterSeed: true, throwOnError: true })]);
  const player = record(actorRowFor(input.encounterId, input.playerId)), counterpart = record(actorRowFor(input.encounterId, input.counterpartId));
  if (!player || !counterpart || [player, counterpart].some(actor => !record(record(actor.computed)?.creature) || !record(actor.item_layer_types)))
    throw new Error('Missing trade participants.');
  return { player, counterpart };
}
export async function exchangePanelGate(input: PanelInput & { training: boolean }): Promise<string | null> {
  if (upper(input.playerId) === upper(input.counterpartId)) return input.training ? 'Invalid training participants.' : 'Invalid trade participants.';
  const { player, counterpart } = await participants(input);
  return exchangePanelRefusal(player, counterpart, input.training, (row, layer) => ftLayerValue(row, layer) != null);
}
export async function exchangePanelFacts(input: PanelInput): Promise<ExchangePanelFacts> {
  const [{ player, counterpart }, facts] = await Promise.all([participants(input), snapshot(input)]);
  const skills = record(record(record(counterpart.computed)?.creature)?.skills) ?? {};
  const favor = pricingFavor(facts.rawFavor, facts.economy);
  const inventory = exchangeInventoryRows(player, counterpart, priceReaders);
  const coins = (root: string) => exchangeCoinStacks(inventory, root, priceReaders, row => ftItemsCol(row, 'item_name'), coinThumbnail);
  return { ...facts, favor, ...favorPrices(favor), tierDescription: favorTier(favor), hasHaggled: hasHaggled(facts.history, input.playerId, input.counterpartId),
    playerCoins: coins(input.playerId), counterpartCoins: coins(input.counterpartId),
    training: Object.fromEntries(Object.keys(skills).map(key => [key, trainingVerdict(player, counterpart, key)])) };
}
export async function submitExchange(input: ExchangeSubmit): Promise<ExchangeAnswer> {
  return runRowOnly(input.playerId, async () => {
    const pending = pendingExchange(input); if (pending) return deliver(pending);
    const [{ player, counterpart }, facts] = await Promise.all([participants(input), snapshot(input)]);
    const inventory = exchangeInventoryRows(player, counterpart, priceReaders);
    const ctx = activityContext(input.encounterId, input.playerId, player, new Set([ecId(player), ecId(counterpart)]));
    ctx.originals = new Map([...ctx.originals, [upper(input.counterpartId), counterpart]]);
    // Hydrate every definition reached by skill/status readers before preflight and the first real die.
    const ids = new Set<string>();
    for (const actor of [player, counterpart, ...inventory]) {
      for (const status of ctx.read.statuses(actor)) ids.add(upper(status.status_item_id));
      const skills = record(record(record(actor.computed)?.creature)?.skills) ?? {};
      for (const entry of Object.values(skills)) { const id = upper(record(entry)?.item_id); if (id) ids.add(id); }
    }
    if (input.kind === 'haggle') {
      for (const s of contract.skillEntries) if (s.variable_name === 'influence' || s.variable_name === 'empathy') ids.add(s.item_id);
    }
    const definitions = new Map<string, Row>(), attempted = new Set<string>();
    const hydrate = async (id: string): Promise<void> => {
      attempted.add(id); const definition = record(await fetchAndCacheObject(id)); if (definition) definitions.set(id, definition);
    };
    await Promise.all([...ids].filter(Boolean).map(hydrate));
    const originalRead = ctx.read;
    ctx.read = { ...originalRead, definition: id => definitions.get(upper(id)) ?? originalRead.definition(id) };
    const read: ExchangeContext['read'] = { ...priceReaders,
      statusItemIds: r => ctx.read.statuses(r).map(s => upper(s.status_item_id)) };
    const decide = () => decideActivity(ctx, fresh => {
      if (!fresh.dryRun && consentPending(fresh.read)) throw new HandBack(TRIGGER_CONSENT_REASON);
      const env: ExchangeContext = { ctx: fresh, player: fresh.members.get(ecId(player))!, counterpart: fresh.members.get(ecId(counterpart))!, inventory,
        facts: { ...facts, rawFavor: input.merchantFavor, economy: input.economy }, read };
      return input.kind === 'trade' ? planTrade(env, input) : input.kind === 'training' ? planTraining(env, input) : planHaggle(env);
    });
    let plan;
    for (;;) {
      try {
        plan = await planWithConsent(input.encounterId, input.playerId, consent => {
          ctx.read.triggerConsent = consent;
          try { return decide(); }
          catch (error) { if (error instanceof HandBack && error.reason === TRIGGER_CONSENT_REASON) return error; throw error; }
        }, result => result instanceof HandBack);
        if (plan instanceof HandBack) throw plan;
        break;
      }
      catch (error) {
        // Missing definitions named by the shared engine are hydrated during dry-run preflight, before any live die.
        const reason = (error as { reason?: string }).reason ?? '';
        const id = reason.match(/:([0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12})$/i)?.[1]?.toUpperCase();
        if (!id || attempted.has(id)) throw error;
        await hydrate(id);
      }
    }
    const { fortunateOffers, ...postedPlan } = plan;
    const command = buildExchangeCommand({ ...input, plan: postedPlan });
    if (fortunateOffers?.length) {
      deferredFortunate.set(command.operation_id, { command, ids: fortunateOffers, saved: false, committed: false });
      if (deferredFortunate.size > 64) retireOffers(deferredFortunate.values().next().value!);
    }
    return deliver(command);
  });
}
Object.assign(window, { ftExchangePanelGate: exchangePanelGate, ftExchangePanelFacts: exchangePanelFacts, ftSubmitExchange: submitExchange });
